import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getWorkOrderConfig } from "../../../../Api/WorkOrderApi";
import { getUserPermissions } from "../../../../Api/RbacApi";
import CODE from "../../../../Static/Constants/StatusCodes";
import { indexByCode } from "../workOrderHelpers";

// The work order config tables, and the one identity the maker/checker rule
// needs.
//
// GET /api/work-orders/config is keyed by TABLE NAME -- workOrderType and
// movementType are real config rows with foreign keys, not orgMeta blobs -- and
// answers with only the keys asked for.
//
//   the queue  -> ["workOrderType"]   the type filter and the row labels
//   the form   -> undefined (BOTH)    the movement dropdown needs movementType's
//                                     sourceEntityTypes / destinationEntityTypes
//
// The queue deliberately does NOT default: movementType carries two JSON
// columns the queue never renders, and pulling them for a dropdown that is not
// on screen is a cost paid on every page load.

const EMPTY = Object.freeze([]);

export const useWorkOrderConfig = (types) => {
    // A new array literal every render would re-run the effect forever, so the
    // dependency is the JOINED STRING and the array is rebuilt from it.
    const typesKey = useMemo(
        () => (Array.isArray(types) && types.length ? types.join(",") : ""),
        [types]
    );

    const [config, setConfig] = useState({ workOrderType: EMPTY, movementType: EMPTY });
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const load = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage("");
        const requested = typesKey ? typesKey.split(",") : undefined;
        const res = await getWorkOrderConfig(requested);
        if (res?.responseStatus?.code === CODE.SUCCESS) {
            setConfig({
                workOrderType: res?.data?.workOrderType || EMPTY,
                movementType: res?.data?.movementType || EMPTY,
            });
        } else {
            setConfig({ workOrderType: EMPTY, movementType: EMPTY });
            setErrorMessage(
                res?.responseStatus?.msg || "Could not load the work order configuration"
            );
        }
        setIsLoading(false);
    }, [typesKey]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            await load();
        })();
        return () => {
            cancelled = true;
        };
    }, [load]);

    const workOrderTypes = config.workOrderType;
    const movementTypes = config.movementType;

    return {
        workOrderTypes,
        movementTypes,
        // Named lookups, never positional: MySQL normalises JSON object key
        // order and nothing here promises a stable sort, so indexing a config
        // list by position would attach the wrong rules to a movement.
        workOrderTypesByCode: useMemo(() => indexByCode(workOrderTypes), [workOrderTypes]),
        movementTypesByCode: useMemo(() => indexByCode(movementTypes), [movementTypes]),
        isLoading,
        errorMessage,
        refetch: load,
    };
};

// --------------------------------------------------------------------------
// The current user's id
// --------------------------------------------------------------------------
//
// Nothing on the client holds one. Login writes name, email, fleetID and role
// into cookies and the RBAC layer keeps only a permissions MAP -- there is no
// userID in Redux, in AppCache or in any cookie.
//
// /api/rbac/me/permissions returns `userId` alongside that map, and it is
// already a client in src/Api/RbacApi.js, so this reuses it rather than adding
// an endpoint. It is NOT a substitute for the backend check: reviewWorkOrder
// refuses a self-review from the token regardless of what the client believes.
// What this buys is the button not being offered, and the REASON being shown
// instead -- see canReviewWorkOrder.
//
// Cached in a module-level promise: the id cannot change inside a session, and
// every mount refetching it would put a request on the critical path of a tab
// switch. A FAILED load is not cached, so a user who was offline when the tab
// first opened is not stuck without review buttons for the rest of the session.
let currentUserIDPromise = null;

export const resetCurrentUserIDCache = () => {
    currentUserIDPromise = null;
};

const fetchCurrentUserID = () => {
    if (!currentUserIDPromise) {
        currentUserIDPromise = getUserPermissions()
            .then((res) => {
                if (res?.responseStatus?.code !== CODE.SUCCESS) {
                    currentUserIDPromise = null;
                    return null;
                }
                // GetData wraps the body, so the payload is one level down --
                // the same path PermissionService reads permissionsMap from.
                const userID = res?.response?.data?.userId;
                if (userID === null || userID === undefined) {
                    currentUserIDPromise = null;
                    return null;
                }
                return userID;
            })
            .catch(() => {
                currentUserIDPromise = null;
                return null;
            });
    }
    return currentUserIDPromise;
};

export const useCurrentUserID = () => {
    const [currentUserID, setCurrentUserID] = useState(null);
    // Distinguishes "not loaded yet" from "loaded and there is none". Until it
    // is known, the review gate is closed rather than open: a button that
    // appears and then disappears is worse than one that appears late.
    const [isResolved, setIsResolved] = useState(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        fetchCurrentUserID().then((userID) => {
            if (!mounted.current) return;
            setCurrentUserID(userID);
            setIsResolved(true);
        });
        return () => {
            mounted.current = false;
        };
    }, []);

    return { currentUserID, isResolved };
};

export default useWorkOrderConfig;
